import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Share2, Copy, Check, Film, Sparkles, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const FORMAT_META = {
  landscape: { label: "16:9 Landscape", best: "YouTube · X · Facebook · LinkedIn" },
  vertical: { label: "9:16 Vertical", best: "TikTok · Reels · Shorts · Stories" },
  square: { label: "1:1 Square", best: "Instagram feed · Facebook feed" },
};

const share = (origin, text) => {
  const url = encodeURIComponent(`${origin}/promo`);
  const t = encodeURIComponent(text);
  return [
    { name: "X / Twitter", href: `https://twitter.com/intent/tweet?text=${t}&url=${url}` },
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${t}` },
    { name: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
    { name: "Reddit", href: `https://www.reddit.com/submit?url=${url}&title=${t}` },
    { name: "WhatsApp", href: `https://api.whatsapp.com/send?text=${t}%20${url}` },
    { name: "Telegram", href: `https://t.me/share/url?url=${url}&text=${t}` },
    { name: "Pinterest", href: `https://pinterest.com/pin/create/button/?url=${url}&description=${t}` },
    { name: "Email", href: `mailto:?subject=${t}&body=${t}%0A%0A${url}` },
  ];
};

const UPLOAD_SITES = [
  { name: "YouTube", href: "https://www.youtube.com/upload", fmt: "landscape / vertical (Shorts)" },
  { name: "TikTok", href: "https://www.tiktok.com/upload", fmt: "vertical" },
  { name: "Instagram", href: "https://www.instagram.com/", fmt: "vertical (Reels) / square (feed)" },
  { name: "Facebook Reels", href: "https://www.facebook.com/reels/create", fmt: "vertical" },
  { name: "Snapchat", href: "https://my.snapchat.com/", fmt: "vertical" },
  { name: "Pinterest Idea Pin", href: "https://www.pinterest.com/pin-builder/", fmt: "vertical" },
  { name: "Vimeo", href: "https://vimeo.com/upload", fmt: "landscape" },
  { name: "Google Ads (YouTube)", href: "https://ads.google.com/", fmt: "landscape / vertical" },
  { name: "Meta Ads Manager", href: "https://adsmanager.facebook.com/", fmt: "all" },
  { name: "TikTok Ads", href: "https://ads.tiktok.com/", fmt: "vertical" },
  { name: "X Ads", href: "https://ads.x.com/", fmt: "landscape / square" },
  { name: "Reddit Ads", href: "https://ads.reddit.com/", fmt: "landscape / square" },
];

export default function Promo() {
  const [manifest, setManifest] = useState(null);
  const [length, setLength] = useState("30");
  const [format, setFormat] = useState("landscape");
  const [copied, setCopied] = useState("");
  const origin = window.location.origin;
  const text = "Powerball Oracle — 7 statistical engines + a GPT-5.2 AI Oracle for your next Powerball pick. $4.99 / 30 days.";

  useEffect(() => {
    fetch("/promo/manifest.json").then((r) => r.json()).then(setManifest).catch(() => toast.error("Promo assets not found"));
  }, []);

  const current = useMemo(
    () => manifest?.videos.find((v) => v.length === length && v.format === format),
    [manifest, length, format]
  );

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(label);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(""), 1500);
  };

  const embed = current ? `<video controls width="640" poster="${origin}${current.poster}" src="${origin}${current.file}"></video>` : "";

  return (
    <div className="min-h-screen bg-ink text-parchment" data-testid="promo-page">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
        <Link to="/" className="label-caps text-parchment/50 hover:text-oracle inline-flex items-center gap-2" data-testid="promo-back-link">
          <ArrowLeft className="w-3 h-3" /> Back to the Oracle
        </Link>
        <div className="mt-6 label-caps text-oracle flex items-center gap-2"><Film className="w-3 h-3" strokeWidth={1.5} /> Press & Promo Kit</div>
        <h1 className="font-heading font-light tracking-tighter text-5xl sm:text-6xl mt-3">
          Spread the <span className="italic text-oracle">Oracle.</span>
        </h1>
        <p className="font-body text-parchment/60 text-sm mt-4 max-w-2xl">
          Two ads, three aspect ratios, AI-narrated. Download the cut you need, or share this page — it previews with the video on every social network.
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <div className="flex flex-wrap gap-2 mb-4">
              {["30", "15"].map((l) => (
                <button key={l} data-testid={`length-${l}`} onClick={() => setLength(l)} className={`label-caps px-4 py-2 rounded-md border transition-colors ${length === l ? "bg-oracle text-ink border-oracle" : "border-white/10 text-parchment/60 hover:text-oracle"}`}>
                  {l}s {l === "30" ? "Main Ad" : "Teaser"}
                </button>
              ))}
              <span className="w-px bg-white/10 mx-1" />
              {Object.entries(FORMAT_META).map(([k, m]) => (
                <button key={k} data-testid={`format-${k}`} onClick={() => setFormat(k)} className={`label-caps px-4 py-2 rounded-md border transition-colors ${format === k ? "bg-oracle text-ink border-oracle" : "border-white/10 text-parchment/60 hover:text-oracle"}`}>
                  {m.label}
                </button>
              ))}
            </div>

            <div className={`bg-black/40 border border-oracle/20 rounded-xl overflow-hidden flex items-center justify-center ${format === "vertical" ? "max-h-[720px]" : ""}`}>
              {current ? (
                <video
                  key={current.file}
                  data-testid="promo-video"
                  controls
                  playsInline
                  poster={current.poster}
                  src={current.file}
                  className={format === "vertical" ? "h-[720px] w-auto" : "w-full"}
                />
              ) : (
                <div className="py-32 label-caps text-parchment/40">{manifest ? "Rendering…" : "Loading…"}</div>
              )}
            </div>
            {current && (
              <div className="mt-3 flex flex-wrap items-center gap-3 font-body text-xs text-parchment/50">
                <span data-testid="promo-video-meta">{current.width}×{current.height} · {current.duration}s · MP4 H.264</span>
                <span className="text-parchment/30">·</span>
                <span>Best for: {FORMAT_META[format].best}</span>
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-3">
              {current && (
                <a href={current.file} download={`${current.name}.mp4`} data-testid="download-current-btn">
                  <Button className="bg-oracle text-ink hover:bg-[#FFC033] label-caps"><Download className="w-4 h-4 mr-2" /> Download this cut</Button>
                </a>
              )}
              <Button data-testid="copy-link-btn" variant="ghost" onClick={() => copy("Link", `${origin}/promo`)} className="text-parchment hover:text-oracle hover:bg-white/5 label-caps">
                {copied === "Link" ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />} Copy share link
              </Button>
              <Button data-testid="copy-embed-btn" variant="ghost" onClick={() => copy("Embed code", embed)} className="text-parchment hover:text-oracle hover:bg-white/5 label-caps">
                <Copy className="w-4 h-4 mr-2" /> Copy embed code
              </Button>
              {navigator.share && (
                <Button data-testid="native-share-btn" variant="ghost" onClick={() => navigator.share({ title: "Powerball Oracle", text, url: `${origin}/promo` }).catch(() => {})} className="text-parchment hover:text-oracle hover:bg-white/5 label-caps">
                  <Share2 className="w-4 h-4 mr-2" /> Share…
                </Button>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="bg-surface border border-white/5 rounded-xl p-6" data-testid="share-links">
              <div className="label-caps text-oracle">Share the link</div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {share(origin, text).map((s) => (
                  <a key={s.name} href={s.href} target="_blank" rel="noreferrer" data-testid={`share-${s.name.split(" ")[0].toLowerCase()}`} className="label-caps text-parchment/80 hover:text-oracle border border-white/10 hover:border-oracle/40 rounded-md px-3 py-2.5 text-center transition-colors">
                    {s.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-surface border border-white/5 rounded-xl p-6" data-testid="download-all">
              <div className="label-caps text-oracle">All cuts</div>
              <ul className="mt-3 divide-y divide-white/5">
                {manifest?.videos.map((v) => (
                  <li key={v.name} className="flex items-center justify-between py-2.5 font-body text-xs">
                    <span className="text-parchment/70">{v.length}s · {FORMAT_META[v.format].label}</span>
                    <a href={v.file} download={`${v.name}.mp4`} data-testid={`download-${v.name}`} className="label-caps text-oracle hover:text-[#FFC033] inline-flex items-center gap-1"><Download className="w-3 h-3" /> MP4</a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-surface border border-white/5 rounded-xl p-6" data-testid="upload-sites">
              <div className="label-caps text-oracle">Upload directly</div>
              <p className="font-body text-[11px] text-parchment/50 mt-1">These platforms need the file uploaded in-app — download the cut, then open:</p>
              <ul className="mt-3 grid grid-cols-1 gap-1.5">
                {UPLOAD_SITES.map((u) => (
                  <li key={u.name} className="flex items-center justify-between font-body text-xs">
                    <a href={u.href} target="_blank" rel="noreferrer" className="text-parchment/80 hover:text-oracle transition-colors">{u.name} ↗</a>
                    <span className="text-parchment/40">{u.fmt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {manifest?.script && (
          <div className="mt-10 bg-black/30 border border-white/5 rounded-xl p-6" data-testid="promo-script">
            <div className="label-caps text-oracle flex items-center gap-2"><Sparkles className="w-3 h-3" /> Narration script · {length}s</div>
            <ol className="mt-3 space-y-2">
              {manifest.script[length].map((line) => (
                <li key={line} className="font-heading italic text-lg text-parchment/85">“{line}”</li>
              ))}
            </ol>
            <Button variant="ghost" onClick={() => copy("Caption", `${text}\n\n${origin}`)} className="mt-4 text-parchment hover:text-oracle hover:bg-white/5 label-caps" data-testid="copy-caption-btn">
              <Copy className="w-4 h-4 mr-2" /> Copy post caption
            </Button>
          </div>
        )}
      </div>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}
